import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { BarChart, BoxplotChart, HeatmapChart, LineChart, PieChart, ScatterChart } from 'echarts/charts';
import {
  DataZoomComponent,
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  VisualMapComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { LabelLayout } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';

import { Box, useMediaQuery } from '@mui/material';

import type { LensEChartsOption } from '../lensEChartsOption';

/**
 * Registered once for the module rather than per chart. Only the pieces Lens
 * draws are pulled in: importing the barrel would land every map, graph and
 * tree series in the bundle for card types the planner cannot produce.
 */
echarts.use([
  BarChart,
  BoxplotChart,
  HeatmapChart,
  LineChart,
  PieChart,
  ScatterChart,
  DataZoomComponent,
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  VisualMapComponent,
  LabelLayout,
  CanvasRenderer,
]);

/** Only used before the first layout pass, and only to keep init from warning. */
const FALLBACK_WIDTH = 640;

/** What a caller outside the chart is allowed to ask the instance for. */
export type LensChartHandle = {
  /** A PNG of the plot as drawn, or null before the first paint. */
  toPngDataUrl: (background: string) => string | null;
};

export type LensEChartClick = {
  seriesName?: string;
  seriesIndex?: number;
  dataIndex?: number;
  name?: string;
};

type Props = {
  option: LensEChartsOption;
  height: number;
  highlightedSeries?: string | null;
  /** Names the plot for a screen reader. The keyboard layer carries the values. */
  ariaLabel?: string;
  onMarkClick?: (params: LensEChartClick) => void;
  onMarkDoubleClick?: (params: LensEChartClick) => void;
  onClearHighlight?: () => void;
  /**
   * Dragging one category past another. Given the index it started on and the
   * one it was dropped on, both against the option's own category order.
   */
  onCategoryDrag?: (from: number, to: number) => void;
};

/**
 * Owns one ECharts instance for the life of the card. The option object is
 * rebuilt on every render upstream, so it is applied with notMerge: a merge
 * would keep series from a previous shape alive when a filter drops one.
 */
const LensEChart = forwardRef<LensChartHandle, Props>(function LensEChart(
  { option, height, ariaLabel, onMarkClick, onMarkDoubleClick, onClearHighlight, onCategoryDrag, highlightedSeries },
  ref,
) {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const hostRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  // Held in a ref so a new handler identity never tears down the instance.
  const clickRef = useRef(onMarkClick);
  clickRef.current = onMarkClick;
  const doubleClickRef = useRef(onMarkDoubleClick);
  doubleClickRef.current = onMarkDoubleClick;
  const clearHighlightRef = useRef(onClearHighlight);
  clearHighlightRef.current = onClearHighlight;
  const dragRef = useRef(onCategoryDrag);
  dragRef.current = onCategoryDrag;
  const seedHeightRef = useRef(height);
  // Where the dragged category would land, and what it is called. Null unless a
  // reorder drag is in flight, so nothing renders the rest of the time.
  const [drop, setDrop] = useState<{ x: number; label: string } | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return undefined;
    }

    // A card can mount before its grid has given it a size, and jsdom always
    // reports zero. Seeding a size keeps the first paint from warning; the
    // observer below corrects it as soon as there is a real box to measure.
    const chart = echarts.init(host, undefined, {
      renderer: 'canvas',
      height: host.clientHeight || seedHeightRef.current,
      width: host.clientWidth || FALLBACK_WIDTH,
    });
    chartRef.current = chart;
    chart.on('click', (params: unknown) => {
      if (clearHighlightRef.current) host.focus({ preventScroll: true });
      const hit = params as LensEChartClick;
      clickRef.current?.(hit);
    });

    chart.on('dblclick', (params: unknown) => doubleClickRef.current?.(params as LensEChartClick));
    chart.getZr().on('click', (event) => {
      if (!event.target) {
        clearHighlightRef.current?.();
      }
    });

    // Reordering by dragging a mark shares its gesture with cross-filtering by
    // clicking one, so the two are told apart by distance: anything under the
    // threshold is a click and never reorders.
    const DRAG_THRESHOLD_PX = 8;
    let start: { index: number; x: number } | null = null;
    let tooltipMuted = false;

    const categoryAt = (x: number): number | null => {
      const index = chart.convertFromPixel({ xAxisIndex: 0 }, x);
      return typeof index === 'number' && Number.isFinite(index) ? Math.round(index) : null;
    };

    const zr = chart.getZr();
    const labelAt = (index: number): string => {
      const axis = (chart.getOption() as { xAxis?: Array<{ data?: Array<unknown> }> }).xAxis?.[0];
      return String(axis?.data?.[index] ?? '');
    };

    const onDown = (event: { offsetX: number; offsetY: number }) => {
      if (!dragRef.current) {
        return;
      }
      const index = categoryAt(event.offsetX);
      start = index === null ? null : { index, x: event.offsetX };
    };

    // Nothing is shown until the pointer has moved far enough to be a drag
    // rather than a click, so a plain cross-filter click never flashes a marker.
    const onMove = (event: { offsetX: number }) => {
      if (!start || !dragRef.current) {
        return;
      }
      if (Math.abs(event.offsetX - start.x) < DRAG_THRESHOLD_PX) {
        setDrop(null);
        return;
      }
      const to = categoryAt(event.offsetX);
      if (to === null) {
        return;
      }
      // The hover tooltip keeps tracking the pointer through a drag, landing on
      // top of the drop marker and naming whichever category is passed under
      // rather than the one being moved. hideTip alone does not hold: the next
      // internal move brings it straight back, so it is switched off for the
      // duration and restored on drop. Two merges per drag, not one per move.
      if (!tooltipMuted) {
        tooltipMuted = true;
        chart.setOption({ tooltip: { show: false } });
      }
      const x = chart.convertToPixel({ xAxisIndex: 0 }, to);
      setDrop(typeof x === 'number' ? { x, label: labelAt(start.index) } : null);
    };

    const onUp = (event: { offsetX: number }) => {
      const from = start;
      start = null;
      setDrop(null);
      if (tooltipMuted) {
        tooltipMuted = false;
        chart.setOption({ tooltip: { show: true } });
      }
      if (!from || !dragRef.current || Math.abs(event.offsetX - from.x) < DRAG_THRESHOLD_PX) {
        return;
      }
      const to = categoryAt(event.offsetX);
      if (to !== null && to !== from.index) {
        dragRef.current(from.index, to);
      }
    };
    zr.on('mousedown', onDown);
    zr.on('mousemove', onMove);
    zr.on('mouseup', onUp);

    // The card is resized by its grid, not by the window, so a window listener
    // would miss every layout change that matters.
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            chart.resize({ height: 'auto', width: 'auto' });
          });
    observer?.observe(host);

    return () => {
      zr.off('mousedown', onDown);
      zr.off('mousemove', onMove);
      zr.off('mouseup', onUp);
      observer?.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    chartRef.current?.setOption(
      reducedMotion
        ? {
            ...option,
            animation: false,
            series: Array.isArray(option.series)
              ? option.series.map((series) => ({ ...series, animation: false }))
              : option.series,
          }
        : option,
      { notMerge: true },
    );
  }, [option, reducedMotion]);

  useEffect(() => {
    if (highlightedSeries === undefined || !Array.isArray(option.series)) return;
    chartRef.current?.setOption({
      series: option.series.map((series) => {
        if (series.type !== 'line') return series;
        const selected =
          highlightedSeries !== null &&
          (series.id === `lens-series:${highlightedSeries}` || series.id === `lens-partial:${highlightedSeries}`);
        const dimmed = highlightedSeries !== null && !selected;
        return {
          id: series.id,
          lineStyle: {
            ...series.lineStyle,
            opacity: dimmed ? 0.2 : (series.lineStyle?.opacity ?? 1),
            width: selected ? 3 : (series.lineStyle?.width ?? 2),
          },
          itemStyle: { ...series.itemStyle, opacity: dimmed ? 0.2 : (series.itemStyle?.opacity ?? 1) },
          ...(series.areaStyle
            ? { areaStyle: { ...series.areaStyle, opacity: (series.areaStyle.opacity ?? 0.7) * (dimmed ? 0.2 : 1) } }
            : {}),
          ...(series.endLabel
            ? {
                endLabel: {
                  ...series.endLabel,
                  opacity: dimmed ? 0.55 : (series.endLabel.opacity ?? 1),
                  fontWeight: selected ? 700 : (series.endLabel.fontWeight ?? 'normal'),
                },
              }
            : {}),
          emphasis: { ...series.emphasis, disabled: highlightedSeries !== null || Boolean(series.emphasis?.disabled) },
        };
      }),
    });
  }, [option, highlightedSeries, reducedMotion]);

  useEffect(() => {
    chartRef.current?.resize({ height: 'auto', width: 'auto' });
  }, [height]);

  useImperativeHandle(
    ref,
    () => ({
      toPngDataUrl: (background: string) =>
        // Twice the CSS size, because the thing people do with an exported chart
        // is paste it into a deck, where a 1x canvas is visibly soft.
        chartRef.current?.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: background }) ?? null,
    }),
    [],
  );

  return (
    <Box sx={{ height, position: 'relative', width: '100%' }}>
      <Box
        aria-label={ariaLabel}
        data-lens-chart=""
        ref={hostRef}
        role="img"
        tabIndex={onClearHighlight ? 0 : undefined}
        sx={{ cursor: drop ? 'grabbing' : undefined, height: '100%', width: '100%' }}
      />
      {drop && (
        // Drawn over the canvas rather than into it: a DOM marker costs no
        // redraw of the plot on every pointer move, and the chart itself is
        // repainted only once, when the drop is committed.
        <Box
          aria-hidden
          sx={{
            borderLeft: 2,
            borderColor: 'primary.main',
            bottom: 0,
            left: drop.x,
            pointerEvents: 'none',
            position: 'absolute',
            top: 0,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'primary.main',
              borderRadius: '8px',
              color: 'primary.contrastText',
              fontSize: 11,
              left: 4,
              position: 'absolute',
              px: 0.75,
              py: 0.25,
              top: 4,
              whiteSpace: 'nowrap',
            }}
          >
            {drop.label}
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default LensEChart;
